import { Api } from "@uikit/service/api";
import { Tooltip, Upload, UploadFile } from "antd";
import { RcFile } from "antd/lib/upload";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { get, map, some } from "lodash";
import { useEffect, useState, useContext } from "react";
import { useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import func from "@uikit/func";
import { DisplayType, UploadType } from "../typing";
import styles from "./DragDropFile.module.scss";
import { UploadLoading } from "./UploadLoading";
import { fileFormatConfig } from "./fileFormatConfig";
import { HomeUsePluginContext } from "@uiview/pages/homeUsePlugin/HomeUsePluginContext";
import isBlank from "@sedan-utils/is-blank";
import store from "@store/index";
import { logEvent } from "@uikit/service/amplitude";
const { Dragger } = Upload;

const api = new Api(false);

export type Percent = { loaded: number; total: number };

type DragDropFileProps = {
  dropUploadFileData?: {
    nodeId: string;
    fileData: UploadFile<any>;
    url?: string;
    filename?: string;
  };
  isLongPress: boolean;
  isInCanvas: boolean;
  id: string;
  checkNodeArr: Array<any>;
  textAreaValue: any;
  canvasType: string;
  asInputNodeChange?: (value: unknown) => void;
  chatAi?: boolean;
  isStaticApp?: boolean;
} & unknown;

export function DragDropFile(props: DragDropFileProps) {
  const homeContext = useContext(HomeUsePluginContext);

  const { setNodes, getNodes } = useReactFlow();

  const featureTags: string[] = get(store.getState().editor.me, "feature_tags", []);
  const creating: boolean = useSelector(state => get(state, "editor.cot.creating", false));
  const saasInputNodeIds: string[] = useSelector(state => get(state, "editor.cot.inputIds", []));
  const currentInputId: string = useSelector(state => get(state, "editor.cot.currentInputId", ""));

  const [file, setFile] = useState<any>();
  const [errorMsg, setErrorMsg] = useState("");
  const [percent, setPercent] = useState<Percent>({ loaded: 0, total: 0 });
  const [loading, setLoading] = useState("");
  const [unStructuredDownloadFailedText, setUnStructuredDownloadFailedText] = useState<string | undefined>();

  const beforeUpload = (file: RcFile) => {
    setFile(file);
    const regex = /(.*)\.([^.]+)$/;
    const match = regex.exec(file.name);
    if (match) {
      logEvent("upload_file", {
        name: match[1],
        extension: match[2],
      });
    }
    return true;
  };

  const removeFile = () => {
    setFile(undefined);
    setPercent({ loaded: 0, total: 0 });
    setErrorMsg("");
    setLoading("");
    // TODO need to abort the upload file
  };

  const getDisplayTypeByFileFormat = (filename: string): { displayType: DisplayType; format: string } => {
    // TODO need to get file format from gpt api
    const [, fileFormat] = /(?:\.([^.]+))?$/.exec(filename) || [];

    if (fileFormat) {
      const uploadType = Object.entries(fileFormatConfig).find(([, { formats }]) => {
        return some(formats, (format: string) => format.toLowerCase() === fileFormat.toLowerCase());
      });
      if (uploadType) {
        const [displayType, { featureTag }] = uploadType as unknown as [
          UploadType,
          {
            formats: string[];
            featureTag: string;
          }
        ];
        if (featureTags.includes(featureTag) !== true) {
          return { displayType, format: fileFormat };
        }
      }
    }

    if (featureTags.includes("disable_upload_node_unStructured")) {
      return { displayType: "uploadFile", format: fileFormat };
    }

    return { displayType: "unStructured", format: fileFormat };
  };
  //cot等待用户上传文件后，继续执行
  const handleCot = () => {
    //如果已经运行结束或者当前inputId为空，不执行后续逻辑
    if (!creating || isBlank(saasInputNodeIds) || isBlank(currentInputId) || currentInputId !== props.id) return;
    const nodes = getNodes();
    const index = saasInputNodeIds.findIndex(x => x === currentInputId);
    if (index < 0) return;
    homeContext?.showAllInputs(saasInputNodeIds, nodes, index + 1);
  };
  const uploadS3 = async () => {
    if (file) {
      const formData = new FormData();
      formData.append("file", file as unknown as Blob, file.name);
      const { displayType, format } = getDisplayTypeByFileFormat(file.name);
      let res: any,
        s3Url: any,
        uuid: string | undefined = undefined,
        needToCreateBlobUrl: string = "",
        _loading: string = "",
        _errorMsg: string = "",
        _unStructuredDownloadFailedText: string = "",
        _displayType = displayType;
      try {
        if (file.size > 50 * 1024 * 1000) {
          throw "We accept the following files, under 50 Mb size";
        }
        _loading = "pending";
        res = await api.uploadUseXhr(`/api/me/upload/studios`, formData, {
          onProgress: (p: Percent) => {
            setPercent(p);
          },
        });
        _loading = "finish";
        if (!res.data) {
          throw "Failed to get s3 path";
        }
        //上传完成继续cot流程
        handleCot();
        s3Url = res?.data?.object_s3_url || res?.data?.s3_https_url || "";
        uuid = res?.data?.uuid;

        needToCreateBlobUrl = `/api/me/upload/studios?url=${encodeURIComponent(s3Url)}`;
        // 整理这块代码时存在疑问
        // 总结如下：(仅供参考)上传不支持类型文件且有unstructured 的feature tag 或者没有unstructured 的feature tag 进来下面的处理
        if (_displayType === "unStructured" || _displayType === "uploadFile") {
          try {
            _loading = "pending";
            setLoading(_loading);
            const result = await api.postWithError(
              "/be/vectordb/indexers/",
              {
                data: {
                  file: res?.data?.object_key,
                  loader: "PDFReader",
                },
                uuid,
              },
              { "Content-Type": "application/json" }
            );
            needToCreateBlobUrl = `/api/me/upload/studios?url=${encodeURIComponent(
              get(result, "data.data.unstructured_path")
            )}`;
            _unStructuredDownloadFailedText = get(result, "data.data.response");
            setUnStructuredDownloadFailedText(_unStructuredDownloadFailedText);
          } catch (error: any) {
            _displayType = "uploadFile";
            // const defaultError = "Unable to generate summary.";
            _unStructuredDownloadFailedText = "";
            setUnStructuredDownloadFailedText(_unStructuredDownloadFailedText);

            const defaultError = "This document is not supported, please delete and upload another file.";
            _errorMsg = typeof error === "string" ? error : defaultError;
            setErrorMsg(_errorMsg);
          } finally {
            _loading = "finish";
            setLoading(_loading);
          }
        }
      } catch (error: any) {
        const defaultError = "Upload failed";
        _displayType = "uploadFile";
        _errorMsg = typeof error === "string" ? error : defaultError;
        setErrorMsg(_errorMsg);
        setLoading(_loading);
      } finally {
        setLoading(_loading);
        const textAreaValue = {
          s3Url,
          file: { name: file.name, size: file.size },
          loading: _loading,
          errorMsg: _errorMsg,
          percent: percent,
          name: file.name,
          size: file.size,
          s3Path: res?.data?.object_key,
          needToCreateBlobUrl,
          unStructuredDownloadFailedText: _unStructuredDownloadFailedText,
          format,
          uuid,
        };
        console.log("🚀 ~ uploadS3 ~ textAreaValue:", textAreaValue);
        setNodes(nodes =>
          map(nodes, node => {
            if (node.id === props.id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  ...(_displayType === "unStructured"
                    ? {}
                    : {
                        initCreateBlobUrl: {
                          objectURL: URL.createObjectURL(file as unknown as Blob),
                          fileBlob: file,
                          loading: false,
                        },
                      }),
                  displayType: _displayType,
                  isLimitExceeded: false,
                  textAreaValue,
                  autoRunEdge: props?.chatAi,
                },
              };
            }
            return node;
          })
        );
        props?.asInputNodeChange?.(textAreaValue);
      }
    }
  };

  useEffect(() => {
    if (!func.isEmpty(file) && file instanceof File) {
      uploadS3();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    const textAreaValue = props.textAreaValue;
    if (!func.isEmpty(textAreaValue)) {
      setFile(textAreaValue.file);
      setErrorMsg(textAreaValue.errorMsg);
      setPercent(textAreaValue.percent);
      setLoading(textAreaValue.loading);
      setUnStructuredDownloadFailedText(textAreaValue.unStructuredDownloadFailedText);
    }
  }, [props.textAreaValue]);

  useEffect(() => {
    if (props.dropUploadFileData && func.isEmpty(props.textAreaValue) && props.isInCanvas === true) {
      const { dropUploadFileData } = props;
      const fileData = dropUploadFileData.fileData;
      const fileUrl = dropUploadFileData.url;
      const filename = dropUploadFileData.filename || "";

      if (fileUrl) {
        fetch(fileUrl)
          .then(response => {
            return response.blob();
          })
          .then(res => {
            setFile(new File([res], filename));
          });
      }
      if (fileData) {
        setFile(fileData);
      }
    }
  }, [props.dropUploadFileData]);

  return (
    <div className={styles.sourceBox}>
      {file === undefined && (
        <>
          <div className={styles.title}>Add a Knowledge Source</div>
          <div className={styles.uploadWrapper}>
            <div className={styles.desc}>Upload files from your computer.</div>
            <SourceTooltip title={<SupportFile />} isPopupContainer={!func.isEmpty(props?.canvasType)}>
              <span className={styles.SupportFormat}>Supported file formats</span>
            </SourceTooltip>
            <Dragger
              name="file"
              className={styles.dragger}
              multiple={false}
              onRemove={removeFile}
              customRequest={() => {}}
              beforeUpload={beforeUpload}
            >
              <div className={styles.upload}>
                <div className={styles.icon}>
                  <img alt="" src={`${settings.S3CDN}${settings.viewAssetsPath}upload.svg`} />
                </div>
                <div className={styles.desc}>Drag and drop your files here to upload them.</div>
                <div className={styles.limitSize}>50 Mb max file size.</div>
              </div>
            </Dragger>
          </div>
        </>
      )}

      {file && (
        <UploadLoading
          fileName={file.name}
          onClose={removeFile}
          percent={percent}
          size={file.size}
          errorMsg={errorMsg}
          unStructuredDownloadFailedText={unStructuredDownloadFailedText}
          loading={loading}
          isInCanvas={props.isInCanvas}
          isStaticApp={props.isStaticApp}
        />
      )}
    </div>
  );
}

function SupportFile() {
  const supportList = ["PDF", "XLSX", "CSV", "TXT", "DOC", "DOCX", "PNG", "JPG", "JPEG", "SVG", "MP4", "MP3", "M4A"];
  return (
    <div className={styles.surportWrapper}>
      {supportList.map(s => (
        <span key={s} className={styles.surportItem}>
          {s}
        </span>
      ))}
    </div>
  );
}

function SourceTooltip({ children, title, isPopupContainer }: any) {
  if (isPopupContainer) {
    return (
      <Tooltip
        placement="top"
        title={title}
        getPopupContainer={() => document.getElementById("AiSaasHeader")!}
        overlayInnerStyle={{
          borderRadius: "10px",
          backgroundColor: "rgba(0, 0, 3, 0.85)",
          padding: "20px 30px",
        }}
      >
        {children}
      </Tooltip>
    );
  }
  return (
    <Tooltip
      placement="top"
      title={title}
      overlayInnerStyle={{
        borderRadius: "10px",
        backgroundColor: "rgba(0, 0, 3, 0.85)",
        padding: "20px 30px",
      }}
    >
      {children}
    </Tooltip>
  );
}
